/**
 * Workflows Management
 *
 * Workflows are runtime orchestration policy objects — they govern cycles
 * throughout their entire lifecycle (allowed session modes, write-back,
 * result resolution, catchball, cascade session defaults).
 *
 * Initialization blueprints (attributes, participants, groups to seed at
 * cycle creation) have moved to Settings → Templates (CycleTemplate).
 * A workflow may optionally reference a default template via the template
 * selector below.
 *
 * Canon: "Workflow is always live. Templates are applied once."
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  GitBranch,
  Archive,
  CheckCircle,
  X,
  RefreshCw,
  Sliders,
  LayoutTemplate,
  Sparkles,
} from 'lucide-react';
import { useMemo } from 'react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useWorkflowStore } from '../../../stores/prioritization/workflowStore';
import { useCycleTemplateStore, selectActiveTemplates } from '../../../stores/prioritization/cycleTemplateStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { workspaceLayerFromWorkspace, getSessionDefaultsInheritanceBreakdown } from '../../../lib/effectiveConfigResolver';
import { SessionDefaultsInheritanceSummary, WorkspaceSessionRulesSection } from '../../../components/prioritization/cycle-config';
import { checkPolicyForWorkflow } from '../../../lib/authorization/storeEnforcement';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import type {
  WorkflowConfiguration,
  AllowedSessionMode,
  ResultResolutionStrategy,
  AttributeWriteBack,
  ResultWriteBack,
} from '../../../types/prioritization/workflow';
import { RankOrderChangeSetting } from '@/components/prioritization/cycle-config/RankOrderChangeSetting';
import { RoomRulesSettings } from '@/components/prioritization/cycle-config/RoomRulesSettings';
import { SESSION_CONTEXT_MODES, SESSION_CONTEXT_MODE_LABELS } from '@/types/prioritization/workflow';

export default function Workflows() {
  const workflows = useWorkflowStore((s) => s.workflows);
  const isLoading = useWorkflowStore((s) => s.isLoading);
  const loadError = useWorkflowStore((s) => s.loadError);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);
  const getWorkspaceDefaultWorkflowId = useWorkflowStore((s) => s.getWorkspaceDefaultWorkflowId);
  const setWorkspaceDefaultWorkflow = useWorkflowStore((s) => s.setWorkspaceDefaultWorkflow);
  const createWorkflow = useWorkflowStore((s) => s.createWorkflow);
  const archiveWorkflow = useWorkflowStore((s) => s.archiveWorkflow);
  const userId = useUserStore((s) => s.userId) ?? 'system';
  const workspaceId = getCurrentWorkspaceId();

  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [setDefaultError, setSetDefaultError] = useState<string | null>(null);
  const [editingConfigWorkflowId, setEditingConfigWorkflowId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<WorkflowConfiguration>({});
  /** The linked template, as the Templates page and cycle creation read it ('' = none). */
  const [editTemplateId, setEditTemplateId] = useState('');
  const updateWorkflow = useWorkflowStore((s) => s.updateWorkflow);

  // Stage 6: Authority — each action configurable from Settings → Roles/Permissions
  const [canCreateWorkflow, setCanCreateWorkflow] = useState(true);
  const [canSetDefaultWorkflow, setCanSetDefaultWorkflow] = useState(true);
  const [canEditWorkflowConfig, setCanEditWorkflowConfig] = useState(true);
  const [canArchiveWorkflow, setCanArchiveWorkflow] = useState(true);
  useEffect(() => {
    if (!workspaceId) {
      setCanCreateWorkflow(true);
      setCanSetDefaultWorkflow(true);
      setCanEditWorkflowConfig(true);
      setCanArchiveWorkflow(true);
      return;
    }
    const data = { workspaceId };
    setCanCreateWorkflow(checkPolicyForWorkflow('workflow.create', workspaceId, data).allowed);
    setCanSetDefaultWorkflow(checkPolicyForWorkflow('workflow.set_default', workspaceId, data).allowed);
    setCanEditWorkflowConfig(checkPolicyForWorkflow('workflow.edit', workspaceId, data).allowed);
    setCanArchiveWorkflow(checkPolicyForWorkflow('workflow.archive', workspaceId, data).allowed);
  }, [workspaceId]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // Load default workflow from DB; fallback to tenant store's workspace when direct fetch fails (e.g. RLS timing)
  const getCurrentWorkspace = useTenantStore((s) => s.getCurrentWorkspace);
  const tenantWorkspaces = useTenantStore((s) => s.workspaces);
  useEffect(() => {
    if (!workspaceId) return;
    getWorkspaceDefaultWorkflowId(workspaceId).then((id) => {
      if (id != null) {
        setDefaultId(id);
        return;
      }
      // Fallback: tenant store's workspace (from fetchWorkspaces) has default_workflow_id
      const ws = getCurrentWorkspace();
      const fallback = ws?.defaultWorkflowId ?? (ws as { default_workflow_id?: string | null })?.default_workflow_id;
      setDefaultId(fallback ?? null);
    });
  }, [workspaceId, getWorkspaceDefaultWorkflowId, getCurrentWorkspace, workflows, tenantWorkspaces]);

  const globalSessionDefaultsBreakdown = useMemo(() => {
    const workspace = getCurrentWorkspace();
    const workspaceLayer = workspaceLayerFromWorkspace(workspace ?? null);
    return getSessionDefaultsInheritanceBreakdown(
      workspaceLayer ?? null,
      null,
      null,
      null,
      { facilitatorLabel: (id) => id }
    );
  }, [getCurrentWorkspace]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || createSubmitting) return;
    setCreateSubmitting(true);
    const created = await createWorkflow({
      name: createName.trim(),
      description: createDesc.trim() || undefined,
      createdBy: userId,
    });
    setCreateSubmitting(false);
    if (created) {
      setCreateName('');
      setCreateDesc('');
      setShowCreate(false);
      openConfigEdit({ id: created.id, configuration: created.configuration });
    }
  };

  const handleSetDefault = async (wfId: string | null) => {
    if (!workspaceId) return;
    setSetDefaultError(null);
    const result = await setWorkspaceDefaultWorkflow(workspaceId, wfId);
    if (result.success) setDefaultId(wfId);
    else setSetDefaultError(result.error ?? 'Failed');
  };

  const activeWorkflows = workflows.filter((w) => w.status !== 'archived');

  // Templates (for linking a default template to a workflow)
  const allTemplates = useCycleTemplateStore((s) => s.templates);
  const fetchTemplates = useCycleTemplateStore((s) => s.fetchTemplates);
  const activeTemplates = useMemo(() => selectActiveTemplates(allTemplates), [allTemplates]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const { users: systemUsers } = useSystemUsers();

  const openConfigEdit = (wf: { id: string; configuration?: WorkflowConfiguration; templateId?: string | null }) => {
    setEditingConfigWorkflowId(wf.id);
    setEditConfig((wf.configuration as WorkflowConfiguration) ?? {});
    setEditTemplateId(wf.templateId ?? wf.configuration?.template_id ?? '');
  };
  const saveConfigEdit = async () => {
    if (!editingConfigWorkflowId) return;
    // The link lives on the workflow's template_id column (what the Templates page sets);
    // cycle creation reads that column first and the configuration copy second. Both are
    // written, so "None" unlinks a template whichever page linked it (S-43).
    const templateId = editTemplateId || null;
    await updateWorkflow(editingConfigWorkflowId, {
      configuration: {
        ...workflows.find((w) => w.id === editingConfigWorkflowId)?.configuration,
        ...editConfig,
        template_id: templateId,
      },
      templateId,
    });
    setEditingConfigWorkflowId(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Workflows"
        subtitle={
          <span>
            Runtime orchestration policy for cycles — modes, write-back, result resolution, and catchball. For initialization blueprints, see{' '}
            <Link to="/prioritization/settings/templates" className="text-primary-600 hover:underline">Templates</Link>.
          </span>
        }
        actions={
          canCreateWorkflow ? (
            <div className="flex items-center gap-2">
              <Link
                to="guided"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/30 hover:bg-primary-100 dark:hover:bg-primary-950/50 transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Guided setup
              </Link>
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={createSubmitting}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                New Workflow
              </button>
            </div>
          ) : undefined
        }
      />

      {/* Session defaults inheritance */}
      <div>
        <SessionDefaultsInheritanceSummary breakdown={globalSessionDefaultsBreakdown} scope="global" />
        <p className="mt-2 text-xs text-[var(--text-ter)]">
          Cycle and track defaults are set on the cycle or track; session values when the session is created.
        </p>
      </div>

      {workspaceId && <WorkspaceSessionRulesSection workspaceId={workspaceId} />}

      {loadError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 text-sm text-[var(--coral)]">
          <p>{loadError}</p>
          {(loadError.includes('401') || loadError.toLowerCase().includes('unauthorized')) && (
            <p className="mt-1 text-xs">Workflows use Supabase RLS; ensure you are signed in.</p>
          )}
        </div>
      )}

      {setDefaultError && (
        <p className="text-sm text-[var(--coral)]">{setDefaultError}</p>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-[var(--text-ter)] py-4">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading workflows…</span>
        </div>
      ) : activeWorkflows.length === 0 ? (
        <div className="bg-[var(--bg-raised)] border border-dashed border-[var(--rule)] p-10 text-center">
          <GitBranch className="mx-auto h-8 w-8 text-[var(--text-ter)] mb-2" />
          <p className="text-sm font-medium text-[var(--text-sec)]">No workflows yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Create one to assign to cycles or set as workspace default.</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
          {activeWorkflows.map((wf) => (
            <div key={wf.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface)]/60 transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[var(--text-pri)]">{wf.name}</span>
                  {defaultId === wf.id && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                      <CheckCircle className="h-3 w-3" />
                      Default
                    </span>
                  )}
                  {(() => {
                    const templateId = wf.templateId ?? wf.configuration?.template_id;
                    if (!templateId) return null;
                    const template = allTemplates.find((t) => t.id === templateId);
                    return (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]">
                        <LayoutTemplate className="h-3 w-3" />
                        {template?.name ?? 'Template linked'}
                      </span>
                    );
                  })()}
                </div>
                {wf.description && (
                  <p className="mt-0.5 text-xs text-[var(--text-ter)] truncate">{wf.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => handleSetDefault(defaultId === wf.id ? null : wf.id)}
                  disabled={!canSetDefaultWorkflow}
                  title={!canSetDefaultWorkflow ? 'No permission to change workspace default' : undefined}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {defaultId === wf.id ? 'Clear default' : 'Set default'}
                </button>
                <button
                  type="button"
                  onClick={() => openConfigEdit(wf)}
                  disabled={!canEditWorkflowConfig}
                  title={!canEditWorkflowConfig ? 'No permission to edit workflow' : 'Edit orchestration policy'}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sliders className="h-3.5 w-3.5" />
                  Config
                </button>
                <button
                  type="button"
                  onClick={() => archiveWorkflow(wf.id)}
                  disabled={!canArchiveWorkflow}
                  title={!canArchiveWorkflow ? 'No permission to archive' : 'Archive'}
                  className="p-1.5 text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Archive className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {workflows.some((w) => w.status === 'archived') && (
        <details className="mt-2">
          <summary className="text-xs text-[var(--text-ter)] cursor-pointer hover:text-[var(--text-sec)] select-none">Show archived</summary>
          <div className="mt-2 bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
            {workflows
              .filter((w) => w.status === 'archived')
              .map((wf) => (
                <div key={wf.id} className="flex items-center justify-between px-4 py-2.5 text-[var(--text-ter)]">
                  <span className="text-sm">{wf.name}</span>
                  <span className="text-xs">Archived</span>
                </div>
              ))}
          </div>
        </details>
      )}

      <p className="text-xs text-[var(--text-ter)]">
        To assign a workflow to a specific cycle, open the cycle and use the workflow control in the header (
        <Link to="/prioritization/cycles" className="text-primary-600 hover:underline">Cycles</Link> → open a cycle).
      </p>

      {/* Edit config modal (Stage 4) */}
      {editingConfigWorkflowId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditingConfigWorkflowId(null)} />
          <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Workflow Configuration</h2>
            <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-2">Behavioral Policies</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Allowed session modes</label>
                {SESSION_CONTEXT_MODES.map((mode) => {
                  const list: AllowedSessionMode[] = Array.isArray(editConfig.allowed_session_modes) ? editConfig.allowed_session_modes : [...SESSION_CONTEXT_MODES];
                  const checked = list.includes(mode);
                  return (
                    <label key={mode} className="flex items-center gap-2 mb-1">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked ? list.filter((m) => m !== mode) : [...list, mode];
                          setEditConfig((c) => ({ ...c, allowed_session_modes: next.length ? next : undefined }));
                        }}
                        className="rounded border-[var(--rule)]"
                      />
                      <span className="text-sm">{SESSION_CONTEXT_MODE_LABELS[mode]}</span>
                    </label>
                  );
                })}
                <p className="text-xs text-[var(--text-ter)] mt-1">Leave all checked for canonical (all allowed).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Which result counts for a track (default)</label>
                <select
                  value={(editConfig.result_resolution as ResultResolutionStrategy) ?? 'latest_published'}
                  onChange={(e) => setEditConfig((c) => ({ ...c, result_resolution: e.target.value as ResultResolutionStrategy }))}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                >
                  <option value="latest_published">Latest published</option>
                  <option value="selected_official">Chosen session</option>
                  {/* S-22: combining results is not built yet — shown, never chosen. */}
                  <option value="aggregate" disabled>Combined (not available yet)</option>
                </select>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={editConfig.allow_track_resolution_override === true}
                    onChange={(e) => setEditConfig((c) => ({ ...c, allow_track_resolution_override: e.target.checked }))}
                    className="rounded border-[var(--rule)]"
                  />
                  <span className="text-sm font-medium text-[var(--text-sec)]">Tracks may choose their own</span>
                </label>
                <p className="text-xs text-[var(--text-ter)] mt-1">When checked, each track&apos;s session defaults show &quot;Which result counts&quot;, and a track&apos;s own choice is used in place of this one.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Attribute write-back</label>
                <select
                  value={(editConfig.attribute_write_back as AttributeWriteBack) ?? 'direct'}
                  onChange={(e) => setEditConfig((c) => ({ ...c, attribute_write_back: e.target.value as AttributeWriteBack }))}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                >
                  <option value="none">none</option>
                  <option value="session_only">session_only</option>
                  <option value="direct">direct</option>
                </select>
                <p className="text-xs text-[var(--text-ter)] mt-1">When a scoring session closes: none — scores are not saved as values; session_only — saved as that session&apos;s values; direct — offered as contributions that someone who approves values can accept as official. The official value never changes on its own.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Result write-back</label>
                <select
                  value={(editConfig.result_write_back as ResultWriteBack) ?? 'to_track'}
                  onChange={(e) => setEditConfig((c) => ({ ...c, result_write_back: e.target.value as ResultWriteBack }))}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                >
                  <option value="none">none</option>
                  <option value="session_only">session_only</option>
                  <option value="to_track">to_track</option>
                </select>
                <p className="text-xs text-[var(--text-ter)] mt-1">When none, Publish Results is disabled in Session Detail.</p>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editConfig.catchball_enabled !== false}
                    onChange={(e) => setEditConfig((c) => ({ ...c, catchball_enabled: e.target.checked }))}
                    className="rounded border-[var(--rule)]"
                  />
                  <span className="text-sm font-medium text-[var(--text-sec)]">Catchball enabled</span>
                </label>
                <p className="text-xs text-[var(--text-ter)] mt-1">When unchecked, Catchball is hidden for cycles using this workflow.</p>
              </div>

              {/* Catchball routing (canon 29 §6) */}
              <div className="border border-[var(--rule)] p-3 space-y-3 bg-[var(--surface)]">
                <p className="text-sm font-semibold text-[var(--text-pri)]">Catchball routing</p>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editConfig.rollout_enabled === true}
                    onChange={(e) => setEditConfig((c) => ({ ...c, rollout_enabled: e.target.checked }))}
                    className="rounded border-[var(--rule)]"
                  />
                  <span className="text-sm font-medium text-[var(--text-sec)]">Rollout / Fan out enabled</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Max children per Split</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={editConfig.ball_split_max_children ?? 5}
                    onChange={(e) => setEditConfig((c) => ({
                      ...c,
                      ball_split_max_children: Math.max(1, Number(e.target.value) || 5),
                    }))}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                  />
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editConfig.require_aggregate_before_pass_up !== false}
                    onChange={(e) => setEditConfig((c) => ({
                      ...c,
                      require_aggregate_before_pass_up: e.target.checked,
                    }))}
                    className="rounded border-[var(--rule)]"
                  />
                  <span className="text-sm font-medium text-[var(--text-sec)]">Require Aggregate before pass-up</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Path adjust policy</label>
                  <select
                    value={editConfig.path_adjust_policy ?? 'holder_remaining_spine'}
                    onChange={(e) => setEditConfig((c) => ({
                      ...c,
                      path_adjust_policy: e.target.value as WorkflowConfiguration['path_adjust_policy'],
                    }))}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                  >
                    <option value="holder_remaining_spine">Ball holder (remaining spine)</option>
                    <option value="cycle_owner_only">Cycle owner / process.edit only</option>
                    <option value="none">None (path frozen after launch)</option>
                  </select>
                  <p className="text-xs text-[var(--text-ter)] mt-1">
                    Controls who may rewrite where the Loop goes from here. Designer Path stays durable.
                  </p>
                </div>
              </div>
            </div>

            {/* ============================================================ */}
            {/* Session Defaults */}
            {/* ============================================================ */}
            <div className="border-t border-[var(--rule)] pt-4 mt-4">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-3">Session Defaults</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Default timing mode</label>
                  <select
                    value={(editConfig.default_timing_mode as string) ?? ''}
                    onChange={(e) => setEditConfig((c) => ({ ...c, default_timing_mode: e.target.value || null }))}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                  >
                    <option value="">System default (live)</option>
                    <option value="live">Live</option>
                    <option value="async">Async</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Default data entry mode</label>
                  <select
                    value={(editConfig.default_entry_mode as string) ?? ''}
                    onChange={(e) => setEditConfig((c) => ({ ...c, default_entry_mode: e.target.value || null }))}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                  >
                    <option value="">System default (facilitator)</option>
                    <option value="facilitator">Facilitator</option>
                    <option value="participants">Participants</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Default facilitator</label>
                  <select
                    value={(editConfig.default_facilitator_id as string) ?? ''}
                    onChange={(e) => setEditConfig((c) => ({ ...c, default_facilitator_id: e.target.value || null }))}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
                  >
                    <option value="">None (set at cycle/track/session)</option>
                    {systemUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* S-33: may the ranking order and cut line change after the run? Saved right away. */}
              {editingConfigWorkflowId && (
                <div className="mt-4 -mx-4 border-t border-[var(--rule)]">
                  <RankOrderChangeSetting
                    scopeKind="workflow"
                    scopeId={editingConfigWorkflowId}
                    ids={{ workspaceId, workflowId: editingConfigWorkflowId }}
                    canEdit={canEditWorkflowConfig}
                  />
                  <RoomRulesSettings
                    scopeKind="workflow"
                    scopeId={editingConfigWorkflowId}
                    ids={{ workspaceId, workflowId: editingConfigWorkflowId }}
                    canEdit={canEditWorkflowConfig}
                  />
                </div>
              )}
              <p className="text-xs text-[var(--text-ter)] mt-2">
                When this workflow is the workspace default, these session defaults apply globally. No need for a separate Defaults page.
              </p>
            </div>

            {/* ============================================================ */}
            {/* Template (initialization blueprint — applied once at cycle creation) */}
            {/* ============================================================ */}
            <div className="border-t border-[var(--rule)] pt-4 mt-4">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-1">Default Template</h3>
              <p className="text-xs text-[var(--text-ter)] mb-3">
                Optionally link a Template that will be applied once when a cycle is created with
                this workflow. The template seeds attributes, participants, and groups into the
                cycle. After creation the cycle owns those values — the template has no further
                authority over it.
              </p>
              <select
                value={editTemplateId}
                onChange={(e) => setEditTemplateId(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] text-sm"
              >
                <option value="">None — no template applied at cycle creation</option>
                {activeTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-[var(--text-ter)]">
                Manage templates in{' '}
                <Link to="/prioritization/settings/templates" className="text-primary-600 hover:underline">
                  Settings → Templates
                </Link>
                .
              </p>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <button type="button" onClick={() => setEditingConfigWorkflowId(null)} className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]">
                Cancel
              </button>
              <button type="button" onClick={saveConfigEdit} className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700">
                Save
              </button>
            </div>
            <button type="button" onClick={() => setEditingConfigWorkflowId(null)} className="absolute top-4 right-4 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !createSubmitting && setShowCreate(false)} />
          <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Create Workflow</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name *</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g. Standard prioritization"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
                <textarea
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="Optional"
                  rows={2}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => !createSubmitting && setShowCreate(false)}
                  className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!createName.trim() || createSubmitting}
                  className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {createSubmitting ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
            <button
              type="button"
              onClick={() => !createSubmitting && setShowCreate(false)}
              className="absolute top-4 right-4 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
